/**
 * Sentry Initialization - CDN Lazy Loaded (Errors Only)
 *
 * This module defers ALL Sentry loading until the browser is idle by loading
 * from CDN via script tag injection. This completely removes Sentry from
 * the Vite bundle dependency graph.
 *
 * IMPORTANT: Uses bundle.min.js (errors only), NOT bundle.tracing.replay.min.js
 *
 * Why errors-only for marketing site?
 * - browserTracingIntegration creates transactions/spans for every fetch/XHR (CPU overhead)
 * - replayIntegration hooks MutationObserver and monitors ALL DOM changes (101s+ main thread!)
 * - These run continuously even with low sample rates, preventing Lighthouse CPU idle
 * - Marketing sites don't need session replay - that's for debugging complex app state
 *
 * Why CDN instead of dynamic import?
 * - Vite's dynamic import still creates a bundle dependency that browsers preload
 * - Script tag injection is truly lazy - no preload hints, no dependency chain
 * - CDN bundles are optimized and cached globally
 *
 * Trade-offs:
 * - ✅ No Sentry in critical path (better Lighthouse scores)
 * - ✅ Zero Sentry code in main bundle
 * - ✅ CDN caching benefits
 * - ✅ No continuous CPU usage from tracing/replay
 * - ⚠️ Errors before idle callback are queued but may be lost if page unloads
 * - ⚠️ No performance metrics (acceptable for marketing site)
 * - ⚠️ No session replay (acceptable for marketing site)
 *
 * @see https://docs.sentry.io/platforms/javascript/install/cdn/
 */

// Sentry CDN bundle version - update periodically
// NOTE: Using bundle.min.js (errors only) for marketing site performance
// bundle.tracing.replay.min.js was causing 101s+ main thread time due to
// continuous MutationObserver monitoring, preventing Lighthouse CPU idle
const SENTRY_VERSION = '8.42.0';
const SENTRY_CDN_URL = `https://browser.sentry-cdn.com/${SENTRY_VERSION}/bundle.min.js`;

// Queue to capture errors before Sentry loads
const errorQueue: Array<{ error: Error; timestamp: number }> = [];
let isInitialized = false;

// Global Sentry type from CDN bundle (bundle.min.js - errors only)
declare global {
  interface Window {
    Sentry?: {
      init: (options: Record<string, unknown>) => void;
      captureException: (error: Error) => void;
      setUser: (user: { id?: string; email?: string } | null) => void;
      addBreadcrumb: (breadcrumb: Record<string, unknown>) => void;
    };
  }
}

/**
 * Capture an error - queues if Sentry not ready, sends immediately if ready.
 */
export function captureException(error: Error): void {
  if (window.Sentry && isInitialized) {
    window.Sentry.captureException(error);
  } else {
    // Queue for later - keep last 10 errors max to avoid memory issues
    errorQueue.push({ error, timestamp: Date.now() });
    if (errorQueue.length > 10) {
      errorQueue.shift();
    }
  }
}

/**
 * Set user context - no-op if Sentry not ready.
 */
export function setUser(user: { id?: string; email?: string } | null): void {
  if (window.Sentry && isInitialized) {
    window.Sentry.setUser(user);
  }
}

/**
 * Add breadcrumb - no-op if Sentry not ready.
 */
export function addBreadcrumb(breadcrumb: {
  category?: string;
  message?: string;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  data?: Record<string, unknown>;
}): void {
  if (window.Sentry && isInitialized) {
    window.Sentry.addBreadcrumb(breadcrumb);
  }
}

/**
 * Load Sentry from CDN via script tag injection.
 * This is truly lazy - no bundle dependency, no preload hints.
 */
function loadSentryFromCDN(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded (e.g., by ad-blocker allowing it through)
    if (window.Sentry) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = SENTRY_CDN_URL;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Sentry CDN'));
    document.head.appendChild(script);
  });
}

/**
 * Initialize Sentry - called after page is idle.
 */
async function initializeSentry(): Promise<void> {
  if (isInitialized) return;

  try {
    // Load Sentry from CDN
    await loadSentryFromCDN();

    if (!window.Sentry) {
      throw new Error('Sentry not available after load');
    }

    // Determine environment from hostname
    const hostname = window.location.hostname;
    const environment =
      hostname === 'wearecnxion.com'
        ? 'production'
        : hostname.includes('staging') || hostname.includes('temp.')
          ? 'staging'
          : 'development';

    window.Sentry.init({
      dsn: 'https://21410930194d2899e0a99c32446d57c8@sentry.cnxion.app/16',
      environment,
      sendDefaultPii: true,
      // ERRORS ONLY - No tracing or replay for marketing site performance
      // browserTracingIntegration + replayIntegration caused 101s+ main thread time
      // by running continuous MutationObserver monitoring, preventing Lighthouse CPU idle
      integrations: [],
      ignoreErrors: [
        'ResizeObserver loop',
        'ResizeObserver loop limit exceeded',
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        'AbortError',
      ],
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
      ],
    });

    isInitialized = true;

    // Flush queued errors
    while (errorQueue.length > 0) {
      const queued = errorQueue.shift();
      if (queued) {
        window.Sentry.captureException(queued.error);
      }
    }

    // eslint-disable-next-line no-console
    console.debug('[Sentry] Initialized from CDN (lazy loaded)');
  } catch (error) {
    // Sentry failed to load (ad-blocker, network issue, etc.)
    // eslint-disable-next-line no-console
    console.debug('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Defer initialization to browser idle time.
 */
function deferToIdle(callback: () => void, timeoutMs = 2000): void {
  if ('requestIdleCallback' in window) {
    (
      window as Window & {
        requestIdleCallback: (cb: IdleRequestCallback, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback(() => callback(), { timeout: timeoutMs });
  } else {
    // Safari fallback: use requestAnimationFrame + setTimeout
    // This waits for next paint, then defers to next tick
    requestAnimationFrame(() => {
      setTimeout(callback, 100);
    });
  }
}

// Set up global error handlers that queue errors before Sentry loads
if (typeof window !== 'undefined') {
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
      captureException(error);
    }
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = event => {
    if (event.reason instanceof Error) {
      captureException(event.reason);
    } else if (event.reason) {
      captureException(new Error(String(event.reason)));
    }
    if (originalOnUnhandledRejection) {
      return originalOnUnhandledRejection.call(window, event);
    }
  };
}

// Schedule lazy initialization
deferToIdle(initializeSentry);

/**
 * Get the Sentry instance (may be undefined if not loaded yet).
 * Use the exported functions (captureException, setUser, etc.) instead when possible.
 */
export function getSentry(): Window['Sentry'] | undefined {
  return window.Sentry;
}

/**
 * Check if Sentry is initialized.
 */
export function isSentryReady(): boolean {
  return isInitialized;
}
